import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import { observer } from "mobx-react";
import { v4 as uuidv4 } from "uuid";
import useStore from "../../helpers/useStore";
import { PatientsContext } from "../../stores/Patients/patients.provider";
import { usePatient, useCreateOrUpdatePatient } from "../../stores/Patients";
import CopaymentExemptionForm, {
  CopaymentExemptionFormValue,
} from "./CopaymentExemptionForm";

const CopaymentExemptionDialog: React.FC = observer(() => {
  const patientStore = useStore(PatientsContext);
  const { copaymentExemptionDialogOpen } = patientStore;
  const { open, patientId } = copaymentExemptionDialogOpen;

  const { data: patient } = usePatient(patientId!, open && !!patientId);
  const createOrUpdatePatient = useCreateOrUpdatePatient();

  const [exemptions, setExemptions] = useState<CopaymentExemptionFormValue[]>(
    [],
  );
  const [saving, setSaving] = useState(false);

  // Initialize with a fresh exemption when dialog opens
  React.useEffect(() => {
    if (open) {
      const currentYear = new Date().getFullYear();
      setExemptions([
        {
          id: uuidv4(),
          startDate: `${currentYear}-01-01`,
          endDate: `${currentYear}-12-31`,
          documentId: null,
          documentName: null,
        },
      ]);
    }
  }, [open]);

  const handleClose = useCallback(() => {
    patientStore.setCopaymentExemptionDialogOpen(false, null);
    setExemptions([]);
  }, [patientStore]);

  const handleAdd = useCallback(() => {
    const currentYear = new Date().getFullYear();
    setExemptions((prev) => [
      ...prev,
      {
        id: uuidv4(),
        startDate: `${currentYear}-01-01`,
        endDate: `${currentYear}-12-31`,
        documentId: null,
        documentName: null,
      },
    ]);
  }, []);

  const handleRemove = useCallback((id: string) => {
    setExemptions((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const handleChange = useCallback(
    (id: string, field: "startDate" | "endDate", value: string | null) => {
      setExemptions((prev) =>
        prev.map((e) => (e.id === id ? { ...e, [field]: value || "" } : e)),
      );
    },
    [],
  );

  const handleSetYear = useCallback((exemptionId: string, year: number) => {
    setExemptions((prev) =>
      prev.map((e) =>
        e.id === exemptionId
          ? { ...e, startDate: `${year}-01-01`, endDate: `${year}-12-31` }
          : e,
      ),
    );
  }, []);

  const handleDocumentUploadSuccess = useCallback(
    (exemptionId: string, documentId: string, documentName: string) => {
      setExemptions((prev) =>
        prev.map((e) =>
          e.id === exemptionId ? { ...e, documentId, documentName } : e,
        ),
      );
    },
    [],
  );

  const handleSave = async () => {
    if (!patient || exemptions.length === 0) return;

    const hasIncomplete = exemptions.some((e) => !e.startDate || !e.endDate);
    if (hasIncomplete) return;

    setSaving(true);
    try {
      const existingExemptions = (patient.copaymentExemptions || []).map(
        (e: any) => ({
          id: e.id,
          startDate: e.startDate,
          endDate: e.endDate,
          documentId: e.documentId || e.document?.id || null,
        }),
      );

      const newExemptions = exemptions.map((e) => ({
        id: e.id,
        startDate: e.startDate!,
        endDate: e.endDate!,
        documentId: e.documentId || undefined,
      }));

      await createOrUpdatePatient.mutateAsync({
        ...patient,
        copaymentExemptions: [...existingExemptions, ...newExemptions],
      } as any);

      handleClose();
    } catch {
      // Error handled by mutation's onError
    } finally {
      setSaving(false);
    }
  };

  if (!open || !patientId) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Zuzahlungsbefreiung fehlt</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Das gespeicherte Rezept ist als zuzahlungsbefreit markiert, aber der
          Patient hat keine passende Befreiung hinterlegt. Möchten Sie jetzt
          eine hinzufügen?
        </Typography>
        <CopaymentExemptionForm
          patientId={patientId}
          exemptions={exemptions}
          onAdd={handleAdd}
          onRemove={handleRemove}
          onChange={handleChange}
          onSetYear={handleSetYear}
          onDocumentUploadSuccess={handleDocumentUploadSuccess}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Überspringen</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={
            saving ||
            exemptions.length === 0 ||
            exemptions.some((e) => !e.startDate || !e.endDate)
          }
        >
          {saving ? <CircularProgress size={20} /> : "Befreiung speichern"}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default CopaymentExemptionDialog;
